/**
 * Sidenav – Bootstrap 3 compatible
 * Left and right side panel with overlay or push-body mode.
 *
 * ── Vanilla / module usage ───────────────────────────────────────────────────
 *
 *  HTML:
 *    <ul id="slide-out" class="sidenav" data-sidenav="left">…</ul>
 *    <button data-sidenav-target="#slide-out">☰</button>
 *
 *  TS:
 *    import { Sidenav } from './sidenav';
 *
 *    const nav = new Sidenav('#slide-out', { side: 'left', mode: 'overlay' });
 *    nav.open();
 *
 *    // OR auto-init from data attributes
 *    Sidenav.autoInit();
 *
 * ── jQuery plugin usage ──────────────────────────────────────────────────────
 *
 *    // Requires jQuery to be present on window (Bootstrap 3 already loads it).
 *    // Import or <script> this file after jQuery.
 *
 *    // Initialise (options are optional)
 *    $('#slide-out').sidenav({ side: 'left', mode: 'overlay' });
 *
 *    // Call methods on an already-initialised element
 *    $('#slide-out').sidenav('open');
 *    $('#slide-out').sidenav('close');
 *    $('#slide-out').sidenav('toggle');
 *    $('#slide-out').sidenav('destroy');
 *
 *    // Retrieve the Sidenav instance
 *    const instance = $('#slide-out').sidenav('instance');
 *
 *    // All calls are chainable (except 'instance' which returns the Sidenav)
 *    $('#slide-out').sidenav('open').addClass('active');
 * ────────────────────────────────────────────────────────────────────────── */
import $ from 'jquery';

export type SidenavSide = 'left' | 'right';
export type SidenavMode = 'overlay' | 'push';

export interface SidenavOptions {
    /** Which side the panel slides in from. Default: 'left' */
    side?: SidenavSide;
    /** 'overlay' draws a backdrop; 'push' shifts the body margin. Default: 'overlay' */
    mode?: SidenavMode;
    /** Width of the panel (any valid CSS value). Default: '280px' */
    width?: string;
    /** z-index of the panel. Default: 1050 */
    zIndex?: number;
    /** CSS class added to <body> while the nav is open. Default: 'sidenav-open' */
    bodyClass?: string;
    /** Selector for close-trigger elements inside the panel. Default: '.sidenav-close' */
    closeSelector?: string;
    /** Called after the panel finishes opening. */
    onOpen?: (instance: Sidenav) => void;
    /** Called after the panel finishes closing. */
    onClose?: (instance: Sidenav) => void;
}

const DEFAULTS: Required<SidenavOptions> = {
    side: 'left',
    mode: 'overlay',
    width: '280px',
    zIndex: 1050,
    bodyClass: 'sidenav-open',
    closeSelector: '.sidenav-close',
    onOpen: () => undefined,
    onClose: () => undefined,
};

export class Sidenav {
    readonly el: HTMLElement;
    private readonly opts: Required<SidenavOptions>;
    private overlay: HTMLElement | null = null;
    private _isOpen = false;

    get isOpen(): boolean {
        return this._isOpen;
    }

    constructor(selector: string | HTMLElement, options: SidenavOptions = {}) {
        const el = selector instanceof HTMLElement
            ? selector
            : document.querySelector<HTMLElement>(selector);

        if(!el) {
            throw new Error('Sidenav: no element found');
        }

        this.el = el;
        this.opts = { ...DEFAULTS, ...options };

        this._applyBaseStyles();
        this._bindHandlers();
    }

    // ── Public API ─────────────────────────────────────────────────────────────

    open(): void {
        if(this._isOpen) return;
        this._isOpen = true;

        this.el.setAttribute('aria-hidden', 'false');
        this.el.classList.add('sidenav--open');
        document.body.classList.add(this.opts.bodyClass);

        if(this.opts.mode === 'overlay') {
            this._showOverlay();
        } else {
            this._applyBodyPush(true);
        }

        this._once(this.el, 'transitionend', () => this.opts.onOpen(this));
    }

    close(): void {
        if(!this._isOpen) return;
        this._isOpen = false;

        this.el.setAttribute('aria-hidden', 'true');
        this.el.classList.remove('sidenav--open');
        document.body.classList.remove(this.opts.bodyClass);

        if(this.opts.mode === 'overlay') {
            this._hideOverlay();
        } else {
            this._applyBodyPush(false);
        }

        this._once(this.el, 'transitionend', () => this.opts.onClose(this));
    }

    toggle(): void {
        this._isOpen ? this.close() : this.open();
    }

    /** Remove all styles and listeners added by this instance. */
    destroy(): void {
        if(this._isOpen) this.close();
        this._removeOverlay();
        this.el.removeAttribute('style');
        this.el.classList.remove('sidenav--open', 'sidenav--left', 'sidenav--right');
    }

    // ── Private helpers ────────────────────────────────────────────────────────

    private _applyBaseStyles(): void {
        const { side, width, zIndex } = this.opts;

        this.el.classList.add(`sidenav--${side}`);
        this.el.style.width = width;
        this.el.style.zIndex = String(zIndex);
        this.el.setAttribute('aria-hidden', 'true');
    }

    private _bindHandlers(): void {
        this.el.addEventListener('click', (e: MouseEvent) => {
            if((e.target as HTMLElement).closest(this.opts.closeSelector)) {
                e.preventDefault();
                this.close();
            }
        });

        document.addEventListener('keydown', (e: KeyboardEvent) => {
            if(e.key === 'Escape' && this._isOpen) this.close();
        });
    }

    private _showOverlay(): void {
        if(!this.overlay) {
            const o = document.createElement('div');
            o.className = 'sidenav-overlay';
            o.style.zIndex = String(this.opts.zIndex - 1);
            o.addEventListener('click', () => this.close());
            document.body.appendChild(o);
            this.overlay = o;
        }

        const ov = this.overlay;
        ov.style.display = 'block';
        // eslint-disable-next-line no-void
        void ov.offsetWidth; // force reflow so CSS transition fires
        ov.style.opacity = '1';
    }

    private _hideOverlay(): void {
        if(!this.overlay) return;
        const ov = this.overlay;
        ov.style.opacity = '0';
        this._once(ov, 'transitionend', () => {
            ov.style.display = 'none';
        });
    }

    private _removeOverlay(): void {
        if(this.overlay) {
            this.overlay.remove();
            this.overlay = null;
        }
    }

    private _applyBodyPush(open: boolean): void {
        const prop = this.opts.side === 'left' ? 'marginLeft' : 'marginRight';
        document.body.style.transition = 'margin 0.3s cubic-bezier(0.4,0,0.2,1)';
        document.body.style[prop] = open ? this.opts.width : '';
    }

    private _once(target: EventTarget, type: string, handler: () => void): void {
        const cb = () => {
            target.removeEventListener(type, cb);
            handler();
        };
        target.addEventListener(type, cb);
    }

    // ── Static ─────────────────────────────────────────────────────────────────

    /**
   * Auto-initialise all elements carrying a `data-sidenav` attribute.
   *
   * Data attributes on the panel element:
   *   data-sidenav="left|right"
   *   data-sidenav-mode="overlay|push"   (default: overlay)
   *   data-sidenav-width="320px"         (default: 280px)
   *
   * Trigger buttons (anywhere in the page):
   *   data-sidenav-target="#slide-out"
   *
   * @returns Map of element IDs to Sidenav instances
   */
    static autoInit(): Map<string, Sidenav> {
        const instances = new Map<string, Sidenav>();

        document.querySelectorAll<HTMLElement>('[data-sidenav]').forEach((el) => {
            const side = (el.dataset['sidenav'] ?? 'left') as SidenavSide;
            const mode = (el.dataset['sidenavMode'] ?? 'overlay') as SidenavMode;
            const width = el.dataset['sidenavWidth'] ?? '280px';

            const instance = new Sidenav(el, { side, mode, width });
            if(el.id) instances.set(el.id, instance);

            if(el.id) {
                document
          .querySelectorAll<HTMLElement>(`[data-sidenav-target="#${el.id}"]`)
          .forEach((btn) => btn.addEventListener('click', () => instance.toggle()));
            }
        });

        return instances;
    }

    // ── jQuery plugin registration ─────────────────────────────────────────────

    /**
   * Register $.fn.sidenav if jQuery is available.
   * Safe to call even in environments without jQuery – it will no-op.
   */
    static registerJQueryPlugin(): void {
        if(typeof window === 'undefined' || !(window as Window & { jQuery?: JQueryStatic }).jQuery) {
            return;
        }

        /** Key used to store the Sidenav instance on each DOM element via $.data */
        const DATA_KEY = 'sidenav';

    /** Methods that return the jQuery object (chainable) */
    type ChainableMethod = 'open' | 'close' | 'toggle' | 'destroy';
    /** Methods that return a value */
    type ValueMethod = 'instance';
    type PluginMethod = ChainableMethod | ValueMethod;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ($.fn as any).sidenav = function pluginFn(
        this: JQuery,
        methodOrOptions?: PluginMethod | SidenavOptions,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ): any {
        // ── Method call: $(...).sidenav('open') ──────────────────────────────
        if(typeof methodOrOptions === 'string') {
            const method = methodOrOptions;

            if(method === 'instance') {
                // Return the instance from the first element only
                return this.first().data(DATA_KEY) as Sidenav | undefined;
            }

            return this.each(function(this: HTMLElement) {
                const instance = $.data(this, DATA_KEY) as Sidenav | undefined;

                if(!instance) {
                    $.error(
                        `Sidenav: cannot call method "${method}" before initialisation. `
                        + 'Call $(el).sidenav(options) first.',
                    );
                    return;
                }

                if(typeof instance[method] !== 'function') {
                    $.error(`Sidenav: unknown method "${method}"`);
                    return;
                }

                instance[method]();
            });
        }

        // ── Init call: $(...).sidenav() or $(...).sidenav({ side: 'right' }) ─
        const options: SidenavOptions = typeof methodOrOptions === 'object'
            ? methodOrOptions
            : {};

        return this.each(function(this: HTMLElement) {
            if($.data(this, DATA_KEY)) {
                // Already initialised – skip silently
                return;
            }

            const instance = new Sidenav(this, options);
            $.data(this, DATA_KEY, instance);
        });
    };
    }
}

// ── Auto-register when jQuery is already on the page ────────────────────────
// Works for both module and script-tag (IIFE/UMD) usage.
Sidenav.registerJQueryPlugin();
